"use client";

/**
 * Pricing Renderer
 * Renders pricing cards in the canvas (builder) and public view.
 */

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Check, Clock, Sparkles, Star, Sunrise, ThumbsUp, Zap } from "lucide-react";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import { formatCentsAsCurrency } from "@/lib/forms/format-price";
import { BADGE_PRESETS, CARD_IMAGE_RENDER_WIDTH, LANDING_IMAGE_RENDER_QUALITY } from "../../../_logic/constants";
import { transformStorageImageUrl } from "../../../_logic/image-cdn";
import type { PricingCard, PricingComponent } from "../../../_types";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { listFormsWithPayment, type FormWithPayment } from "../../../_logic/actions/form-actions";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SmartButton } from "../../public/SmartButton";
import { SectionContainer } from "./_shared/SectionContainer";

/** Maps badge preset icon names to Lucide icon components */
const BADGE_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Star,
  Sparkles,
  ThumbsUp,
  Zap,
  Clock,
  Sunrise,
};

/** Resolves a badge value to its preset label and icon, or falls back to raw string */
function resolveBadge(badge: string): { label: string; Icon?: React.ComponentType<{ className?: string }> } {
  const preset = BADGE_PRESETS.find((p) => p.value === badge);
  if (preset) return { label: preset.label, Icon: BADGE_ICON_MAP[preset.icon] };
  return { label: badge };
}

/**
 * Resolves a card's display price:
 * - No linked form: the card's own static price text.
 * - Linked to a specific respondent type: that type's price (or "Free").
 * - Linked to a form only (no specific type): the form's default respondent-type
 *   price labeled "From $X" (a floor, not the only price) when the form has types
 *   defined; otherwise falls back to the legacy flat `payment_amount_display`.
 */
function resolveDisplayPrice(card: PricingCard, formsById: Map<number, FormWithPayment>): string | undefined {
  if (!card.formId) return card.price;
  const form = formsById.get(card.formId);
  if (!form) return card.price;

  if (card.respondentTypeId != null) {
    const type = form.respondentTypes.find((t) => t.id === card.respondentTypeId);
    if (!type) return card.price;
    return type.priceCents != null ? formatCentsAsCurrency(type.priceCents, type.currency) : "Free";
  }

  if (form.respondentTypes.length > 0) {
    const defaultType = form.respondentTypes.find((t) => t.isDefault) ?? form.respondentTypes[0];
    return defaultType.priceCents != null
      ? `From ${formatCentsAsCurrency(defaultType.priceCents, defaultType.currency)}`
      : "Free";
  }

  return form.paymentAmountDisplay ?? card.price;
}

interface PricingRendererProps {
  component: PricingComponent;
}

/**
 * Renders a pricing section with card grid.
 */
export function PricingRenderer({ component }: PricingRendererProps): React.ReactElement {
  const { title, items = [] } = component;
  const { primaryColor, eventId } = useBuilderContext();
  const { isPublic } = usePublicViewContext();

  // States
  const [formsById, setFormsById] = useState<Map<number, FormWithPayment>>(new Map());

  // Derived values
  const hasFormLinkedCards = items.some((c) => c.formId);
  const displayItems = items;

  // Effects — fetch current form + respondent-type prices for form-linked cards
  useEffect(() => {
    if (!hasFormLinkedCards) return;
    listFormsWithPayment(eventId).then((forms) => {
      setFormsById(new Map(forms.map((f) => [f.formId, f])));
    });
  }, [eventId, hasFormLinkedCards]);

  // Render
  return (
    <SectionContainer
      maxWidth="4xl"
      background={component.background}
      title={title || ""}
      componentId={component.id}
      titleFieldId="pricing-title"
    >
      {items.length === 0 ? null : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap justify-center gap-6">
            {displayItems.map((card) => (
              <PricingCardDisplay
                key={card.id}
                card={card}
                componentId={component.id}
                primaryColor={primaryColor}
                isPublic={isPublic}
                eventId={eventId}
                formsById={formsById}
              />
            ))}
          </div>
        </div>
      )}
    </SectionContainer>
  );
}

// ============================================================
// Sub-components
// ============================================================

interface PricingCardDisplayProps {
  card: PricingCard;
  componentId: string;
  primaryColor: string;
  isPublic: boolean;
  eventId: number;
  formsById: Map<number, FormWithPayment>;
}

/** Renders a single pricing card with optional image, badge, price, features, and CTA */
function PricingCardDisplay({
  card,
  componentId,
  primaryColor,
  isPublic,
  eventId,
  formsById,
}: PricingCardDisplayProps): React.ReactElement {
  const accentColor = card.color ?? primaryColor;
  const fieldClick = useCanvasFieldClick(componentId);
  const features = card.features ?? [];
  const displayPrice = resolveDisplayPrice(card, formsById);
  const accentTextColor = getButtonTextColor(accentColor);
  const widthClass = isPublic
    ? "sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)]"
    : "@sm:w-[calc(50%-0.75rem)] @md:w-[calc(33.333%-1rem)]";

  const gradientStyle = card.imageUrl
    ? undefined
    : {
        background: `linear-gradient(135deg, ${accentColor}99, ${accentColor}33)`,
      };
  const imageSrc = useMemo(
    () =>
      card.imageUrl
        ? transformStorageImageUrl(card.imageUrl, {
            width: CARD_IMAGE_RENDER_WIDTH,
            quality: LANDING_IMAGE_RENDER_QUALITY,
          })
        : "",
    [card.imageUrl]
  );

  return (
    <div
      className={`flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white ${widthClass}${!isPublic ? " cursor-pointer transition-shadow hover:shadow-md" : ""}`}
      onClick={!isPublic ? fieldClick(`pricing-item:${card.id}`) : undefined}
    >
      {/* Image / Gradient header */}
      <div className="relative h-28 w-full">
        {card.imageUrl ? (
          <img src={imageSrc} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
        ) : (
          <div className="h-full w-full" style={gradientStyle} />
        )}
        {/* Badge */}
        {card.badge &&
          (() => {
            const { label, Icon } = resolveBadge(card.badge);
            return (
              <span
                className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                style={{ backgroundColor: accentColor, color: accentTextColor }}
              >
                {Icon && <Icon className="h-3 w-3" />}
                {label}
              </span>
            );
          })()}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-3 p-5">
        {/* Title + Subtitle */}
        <div className="flex flex-col gap-0.5">
          {/* This card is its own opaque white surface (see the wrapper's `bg-white` above), so
              its text stays plain gray regardless of the section's own background — it's never
              actually sitting on the section's color, unlike content that renders directly on it. */}
          <h3 className="text-lg font-semibold text-gray-900">{card.title || "Untitled"}</h3>
          {card.subtitle && <p className="text-sm font-medium text-gray-500">{card.subtitle}</p>}
        </div>

        {/* Price */}
        {displayPrice && (
          <p className="text-2xl font-bold" style={{ color: accentColor }}>
            {displayPrice}
          </p>
        )}

        {/* Description */}
        {card.description && <p className="text-sm font-medium text-gray-600">{card.description}</p>}

        {/* Features */}
        {features.length > 0 && (
          <ul className="flex flex-col gap-1.5">
            {features.map((feature, i) => (
              <li key={i} className="flex items-start gap-2 text-sm font-medium text-gray-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" aria-hidden="true" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* CTA Button */}
      {card.ctaText && (
        <div className="px-5 pb-5">
          {isPublic && card.ctaLink ? (
            <SmartButton
              href={card.ctaLink}
              label={card.ctaText}
              primaryColor={accentColor}
              eventId={eventId}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90"
              style={{ backgroundColor: accentColor, color: accentTextColor }}
              orderContext={{ title: card.title, description: card.description }}
            />
          ) : (
            <span
              className="flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold"
              style={{ backgroundColor: accentColor, color: accentTextColor }}
            >
              {card.ctaText}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
